import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, Minus, Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, eur, imgW } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";

const NAMES = new Intl.DisplayNames(["en"], { type: "region" });

function Field({ id, label, value, onChange, type = "text", required = true, autoComplete }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{label}</Label>
      <Input id={id} type={type} value={value} required={required} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)}
        className="h-12 rounded-none border-concrete focus-visible:ring-ink" data-testid={`checkout-${id}-input`} />
    </div>
  );
}

export default function Checkout() {
  const { t, lang, cart, updateQty, removeItem, changeSize } = useStore();
  useSeo({ title: t("checkout") });
  const [countries, setCountries] = useState(["PT"]);
  const [form, setForm] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cb_checkout")) || {}; } catch { return {}; }
  });
  const [country, setCountry] = useState(form.country || "PT");
  const [method, setMethod] = useState("standard");
  const [codeInput, setCodeInput] = useState("");
  const [code, setCode] = useState(null);
  const [quote, setQuote] = useState(null);
  const [qErr, setQErr] = useState(null);
  const [paying, setPaying] = useState(false);
  const [sizeMap, setSizeMap] = useState({});

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { localStorage.setItem("cb_checkout", JSON.stringify({ ...form, country })); }, [form, country]);
  useEffect(() => { api.get("/shipping/countries").then((r) => setCountries(r.data.countries)); }, []);
  useEffect(() => {
    const ids = [...new Set(cart.map((c) => c.slug))];
    ids.forEach((slug) => !sizeMap[slug] && api.get(`/products/${slug}`).then((r) => setSizeMap((m) => ({ ...m, [slug]: r.data.product.sizes }))).catch(() => {}));
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(() => cart.map((c) => ({ product_id: c.product_id, size: c.size, qty: c.qty })), [cart]);
  const ikey = JSON.stringify(items);
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    api.post("/cart/quote", { items, country, shipping_method: method, discount_code: code })
      .then((r) => { setQuote(r.data); setQErr(null); if (r.data.discount_error && code) { toast.error(r.data.discount_error); setCode(null); } })
      .catch((e) => setQErr(errMsg(e)));
  }, [ikey, country, method, code]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      const { data } = await api.post("/checkout", {
        items, country, shipping_method: method, discount_code: code, email: form.email, lang, origin_url: window.location.origin,
        address: { first_name: form.first_name, last_name: form.last_name, line1: form.line1, line2: form.line2 || "", city: form.city, postal_code: form.postal_code, country, phone: form.phone || "" },
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setPaying(false);
    }
  };

  if (!cart.length) {
    return (
      <main className="container-x py-32" data-testid="checkout-empty">
        <h1 className="font-display text-6xl font-black uppercase">{t("emptyBag")}</h1>
        <Link to="/shop" className="btn-ink mt-8" data-testid="checkout-empty-shop-btn">{t("shopNow")}</Link>
      </main>
    );
  }

  return (
    <main className="bg-sun pb-24" data-testid="checkout-page">
      <div className="container-x pt-12">
        <h1 className="font-display text-5xl font-black uppercase tracking-tight sm:text-7xl">{t("checkout")}</h1>
      </div>
      <form onSubmit={pay} className="container-x mt-12 grid gap-14 lg:grid-cols-12">
        <div className="space-y-12 lg:col-span-7">
          <section>
            <p className="eyebrow mb-5">01 — {t("contact")}</p>
            <Field id="email" label={t("email")} type="email" value={form.email || ""} onChange={set("email")} autoComplete="email" />
          </section>
          <section>
            <p className="eyebrow mb-5">02 — {t("shippingAddress")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="first_name" label={t("firstName")} value={form.first_name || ""} onChange={set("first_name")} autoComplete="given-name" />
              <Field id="last_name" label={t("lastName")} value={form.last_name || ""} onChange={set("last_name")} autoComplete="family-name" />
              <div className="sm:col-span-2"><Field id="line1" label={t("address1")} value={form.line1 || ""} onChange={set("line1")} autoComplete="address-line1" /></div>
              <div className="sm:col-span-2"><Field id="line2" label={t("address2")} value={form.line2 || ""} onChange={set("line2")} required={false} autoComplete="address-line2" /></div>
              <Field id="city" label={t("city")} value={form.city || ""} onChange={set("city")} autoComplete="address-level2" />
              <Field id="postal_code" label={t("postal")} value={form.postal_code || ""} onChange={set("postal_code")} autoComplete="postal-code" />
              <div className="space-y-1.5">
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{t("country")}</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="h-12 rounded-none border-concrete" data-testid="checkout-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72 rounded-none">
                    {countries.map((c) => <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{NAMES.of(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field id="phone" label={t("phone")} type="tel" value={form.phone || ""} onChange={set("phone")} required={false} autoComplete="tel" />
            </div>
          </section>
          <section>
            <p className="eyebrow mb-5">03 — {t("delivery")}</p>
            <RadioGroup value={method} onValueChange={setMethod} className="gap-0" data-testid="shipping-methods">
              {(quote?.shipping_options || []).map((o) => (
                <label key={o.id} className={`flex cursor-pointer items-center gap-4 border p-5 transition-colors ${method === o.id ? "border-ink" : "border-concrete"} -mt-px`}>
                  <RadioGroupItem value={o.id} data-testid={`shipping-${o.id}`} />
                  <span className="flex-1 text-sm">{o.label}{o.free_over && o.cost > 0 && <span className="block text-xs text-ink/50">{t("free")} &gt; {eur(o.free_over, lang)}</span>}</span>
                  <span className="font-mono text-sm">{o.cost === 0 ? t("free") : eur(o.cost, lang)}</span>
                </label>
              ))}
            </RadioGroup>
          </section>
        </div>

        <aside className="lg:col-span-5">
          <div className="bg-concrete/50 p-6 sm:p-8 lg:sticky lg:top-28" data-testid="order-summary">
            <p className="eyebrow mb-6">{t("orderSummary")}</p>
            <div className="max-h-[340px] space-y-5 overflow-y-auto pr-1">
              {cart.map((it, i) => (
                <div key={`${it.product_id}-${it.size}`} className="flex gap-4" data-testid={`summary-item-${i}`}>
                  <div className="h-16 w-16 shrink-0 overflow-hidden bg-white"><img src={imgW(it.image, 200)} alt={it.name} className="h-full w-full object-contain" /></div>
                  <div className="flex-1 text-sm">
                    <p className="font-medium leading-tight">{it.name}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Select value={it.size} onValueChange={(v) => changeSize(it.product_id, it.size, v, sizeMap[it.slug]?.find((s) => s.size === v)?.stock || 1)}>
                        <SelectTrigger className="h-8 w-[92px] rounded-none border-ink/15 bg-sun font-mono text-xs" data-testid={`summary-size-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          {(sizeMap[it.slug] || [{ size: it.size, stock: 1 }]).map((s) => <SelectItem key={s.size} value={s.size} disabled={s.stock <= 0}>EU {s.size}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center border border-ink/15 bg-sun">
                        <button type="button" className="p-1.5" onClick={() => updateQty(it.product_id, it.size, it.qty - 1)} disabled={it.qty <= 1} aria-label="Decrease" data-testid={`summary-dec-${i}`}><Minus className="h-3 w-3" /></button>
                        <span className="w-6 text-center font-mono text-xs">{it.qty}</span>
                        <button type="button" className="p-1.5" onClick={() => updateQty(it.product_id, it.size, it.qty + 1)} aria-label="Increase" data-testid={`summary-inc-${i}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <button type="button" onClick={() => removeItem(it.product_id, it.size)} className="ml-auto font-mono text-[10px] uppercase text-ink/50 hover:text-ink" data-testid={`summary-remove-${i}`}>{t("remove")}</button>
                    </div>
                  </div>
                  <p className="font-mono text-sm">{eur(it.price * it.qty, lang)}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-2 border-t border-ink/10 pt-6">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder={t("discountCode")} className="h-11 rounded-none border-ink/15 bg-sun pl-9 font-mono uppercase" data-testid="discount-code-input" />
              </div>
              <button type="button" onClick={() => setCode(codeInput.trim() || null)} className="btn-line h-11 px-5 py-0 hover:bg-ink hover:text-sun" data-testid="discount-apply-btn">{t("apply")}</button>
            </div>
            {quote?.discount_applied && <p className="mt-2 font-mono text-xs text-ink" data-testid="discount-applied">✓ {quote.discount_applied.code}</p>}
            {qErr && <p className="mt-3 text-sm text-ink" data-testid="quote-error">{qErr}</p>}
            <dl className="mt-6 space-y-2.5 text-sm">
              <div className="flex justify-between"><dt>{t("subtotal")}</dt><dd className="font-mono" data-testid="summary-subtotal">{quote ? eur(quote.subtotal, lang) : "—"}</dd></div>
              {quote?.discount > 0 && <div className="flex justify-between text-ink"><dt>{t("discount")}</dt><dd className="font-mono" data-testid="summary-discount">−{eur(quote.discount, lang)}</dd></div>}
              <div className="flex justify-between"><dt>{t("shipping")}</dt><dd className="font-mono" data-testid="summary-shipping">{quote ? (quote.shipping === 0 ? t("free") : eur(quote.shipping, lang)) : "—"}</dd></div>
              <div className="flex items-baseline justify-between border-t border-ink/10 pt-4"><dt className="eyebrow">{t("total")}</dt><dd className="font-display text-3xl font-black" data-testid="summary-total">{quote ? eur(quote.total, lang) : "—"}</dd></div>
            </dl>
            <p className="mt-1 text-right text-[11px] text-ink/50">{t("vatIncl")}</p>
            <button type="submit" disabled={paying || !quote || !!qErr} className="btn-ink mt-6 w-full py-5" data-testid="pay-now-btn">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {t("payNow")}
            </button>
            <p className="mt-3 text-center text-xs text-ink/50">{t("securePay")}</p>
          </div>
        </aside>
      </form>
    </main>
  );
}
